import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import theme from "./constants/theme";
import shadowResetGlobalStyles from "./constants/shadowResetGlobalStyles";
import { GlobalStyles, ThemeProvider } from "@mui/material";
import { Provider } from "react-redux";
import store from "./store/store";
import { BrowserRouter } from "react-router-dom";
import "./constants/localization/config";

const root = ReactDOM.createRoot(document.getElementById("root"));

// Suppress browser extension errors - Comprehensive approach
// This must run BEFORE any other code to catch extension errors early
(function() {
  'use strict';
  
  // Helper function to check if error is from extension or should be suppressed
  const isExtensionError = (message, stack, filename) => {
    if (!message && !stack && !filename) return false;
    
    const errorString = `${message || ''} ${stack || ''} ${filename || ''}`.toLowerCase();
    
    // Check for extension indicators
    const hasExtensionIndicator = (
      errorString.includes('contentscript') ||
      errorString.includes('extension://') ||
      errorString.includes('chrome-extension://') ||
      errorString.includes('moz-extension://') ||
      errorString.includes('safari-extension://') ||
      errorString.includes('edge-extension://') ||
      errorString.includes('aggiiclaiamajehmlfpkjmlbadmkledi') // Specific extension ID
    );
    
    // Check for querySelector null errors (common extension issue)
    const isQuerySelectorNullError = (
      errorString.includes('queryselector') && 
      (errorString.includes('null') || errorString.includes('cannot read properties'))
    );
    
    // Check for Wake Lock permission errors (should be suppressed)
    const isWakeLockError = (
      errorString.includes('wake lock') ||
      errorString.includes('wakelock') ||
      errorString.includes('wake lock permission') ||
      errorString.includes('notallowederror') && errorString.includes('wake')
    );
    
    return hasExtensionIndicator || isQuerySelectorNullError || isWakeLockError;
  };
  
  // Store original console methods BEFORE overriding
  const originalError = console.error;
  const originalWarn = console.warn;
  const originalLog = console.log;
  
  // Override console.error to filter extension errors and Wake Lock errors
  console.error = function(...args) {
    try {
      const message = args.map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message + ' ' + (arg.stack || '');
        return String(arg);
      }).join(' ');
      
      const stack = new Error().stack || '';
      
      // Check for Wake Lock errors in message or error objects
      const isWakeLockError = args.some(arg => {
        if (arg instanceof Error) {
          const errorName = arg.name || '';
          const errorMessage = arg.message || '';
          return (
            errorName === 'NotAllowedError' && 
            (errorMessage.toLowerCase().includes('wake lock') || errorMessage.toLowerCase().includes('wakelock'))
          );
        }
        return false;
      }) || (
        message.toLowerCase().includes('wake lock') || 
        message.toLowerCase().includes('wakelock') ||
        message.toLowerCase().includes('notallowederror') && message.toLowerCase().includes('wake')
      );
      
      // Check all arguments for extension errors
      const hasExtensionError = args.some(arg => {
        if (arg instanceof Error) {
          return isExtensionError(arg.message, arg.stack || '', '');
        }
        return false;
      }) || isExtensionError(message, stack, '');
      
      // Suppress Wake Lock errors and extension errors
      if (!hasExtensionError && !isWakeLockError) {
        originalError.apply(console, args);
      }
    } catch (e) {
      // Fallback to original if our filter fails
      originalError.apply(console, args);
    }
  };
  
  // Override console.warn to filter extension warnings and Wake Lock warnings
  console.warn = function(...args) {
    try {
      const message = args.map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg instanceof Error) return arg.message + ' ' + (arg.stack || '');
        return String(arg);
      }).join(' ');
      
      const stack = new Error().stack || '';
      
      // Check for Wake Lock errors in message or error objects
      const isWakeLockError = args.some(arg => {
        if (arg instanceof Error) {
          const errorName = arg.name || '';
          const errorMessage = arg.message || '';
          return (
            errorName === 'NotAllowedError' && 
            (errorMessage.toLowerCase().includes('wake lock') || errorMessage.toLowerCase().includes('wakelock'))
          );
        }
        return false;
      }) || (
        message.toLowerCase().includes('wake lock') || 
        message.toLowerCase().includes('wakelock') ||
        message.toLowerCase().includes('notallowederror') && message.toLowerCase().includes('wake')
      );
      
      if (!isExtensionError(message, stack, '') && !isWakeLockError) {
        originalWarn.apply(console, args);
      }
    } catch (e) {
      originalWarn.apply(console, args);
    }
  };
  
  // Global error event listener with capture phase (runs first)
  window.addEventListener('error', function(event) {
    try {
      const filename = event.filename || '';
      const message = event.message || '';
      const stack = event.error?.stack || '';
      const errorName = event.error?.name || '';
      
      // Check if it's a Wake Lock error by name or message
      const isWakeLockError = 
        errorName === 'NotAllowedError' && 
        (message.toLowerCase().includes('wake lock') || message.toLowerCase().includes('wakelock'));
      
      if (isExtensionError(message, stack, filename) || isWakeLockError) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        return false;
      }
    } catch (e) {
      // Ignore errors in error handler
    }
  }, true); // Capture phase - runs before other handlers
  
  // Global unhandled rejection handler
  window.addEventListener('unhandledrejection', function(event) {
    try {
      const reason = event.reason;
      const message = reason?.message || reason?.toString() || '';
      const stack = reason?.stack || '';
      const errorName = reason?.name || '';
      const reasonString = reason?.toString() || String(reason) || '';
      
      // Check if it's a Wake Lock error
      const isWakeLockError = 
        errorName === 'NotAllowedError' && 
        (message.toLowerCase().includes('wake lock') || message.toLowerCase().includes('wakelock'));
      
      // Check if it's a timeout error (string "Timeout" or timeout in message)
      const isTimeoutError = 
        message.toLowerCase().includes('timeout') ||
        reasonString.toLowerCase().includes('timeout') ||
        reason === 'Timeout' ||
        message === 'Timeout';
      
      if (isExtensionError(message, stack, '') || isWakeLockError || isTimeoutError) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        // For timeout errors, we'll let component-level handlers show user-friendly messages
        // This just prevents the React error overlay from showing
        return false;
      }
    } catch (e) {
      // Ignore errors in error handler
    }
  }, true);
  
  // Intercept errors at the window level (most reliable)
  const originalOnerror = window.onerror;
  window.onerror = function(message, source, lineno, colno, error) {
    try {
      const stack = error?.stack || '';
      const errorName = error?.name || '';
      const errorMessage = message || '';
      
      // Check if it's a Wake Lock error
      const isWakeLockError = 
        errorName === 'NotAllowedError' && 
        (errorMessage.toLowerCase().includes('wake lock') || errorMessage.toLowerCase().includes('wakelock'));
      
      if (isExtensionError(message, stack, source) || isWakeLockError) {
        return true; // Suppress the error
      }
    } catch (e) {
      // Ignore errors in error handler
    }
    
    // Call original handler if exists
    if (originalOnerror) {
      try {
        return originalOnerror.call(this, message, source, lineno, colno, error);
      } catch (e) {
        return false;
      }
    }
    return false;
  };
  
  // Also catch errors that might slip through
  const originalAddEventListener = EventTarget.prototype.addEventListener;
  EventTarget.prototype.addEventListener = function(type, listener, options) {
    // Don't interfere with normal event listeners
    return originalAddEventListener.call(this, type, listener, options);
  };
})();

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

// Firebase Service Worker registration - Only if Firebase is configured
if ('serviceWorker' in navigator && process.env.REACT_APP_FIREBASE_API_KEY) {
  navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: '/'
  })
    .catch(function (error) {
      // Silent fail - this is expected when Firebase is not configured
    });
}

root.render(
  <Provider store={store}>
    {/* <React.StrictMode> */}
    <ThemeProvider theme={theme}>
      <GlobalStyles styles={shadowResetGlobalStyles} />
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </ThemeProvider>
    {/* </React.StrictMode> */}
  </Provider>
);
